import React, { useState } from 'react';
import { ShieldCheck, Loader2, Key, Mail, Lock } from 'lucide-react';
import { API_URL } from '../services/api';

interface ActivationScreenProps {
    onActivated: () => void;
}

const ActivationScreen: React.FC<ActivationScreenProps> = ({ onActivated }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [licenseCode, setLicenseCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleActivate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password || !licenseCode) {
            setError('الرجاء تعبئة جميع الحقول');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/activate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, licenseCode })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                onActivated();
            } else {
                setError(data.error || 'فشل التفعيل. الرجاء التأكد من البيانات.');
            }
        } catch (err) {
            setError('خطأ في الاتصال بالخادم. تأكد من تشغيل النظام بشكل صحيح.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-md w-full p-8 text-center animate-fadeIn">
                <div className="mx-auto bg-fly-50 w-20 h-20 rounded-full flex items-center justify-center mb-6">
                    <ShieldCheck className="w-10 h-10 text-fly-600" />
                </div>
                
                <h1 className="text-2xl font-bold text-slate-800 mb-2">تفعيل النظام</h1>
                <p className="text-slate-500 text-sm mb-8">
                    أدخل بيانات حسابك وكود التفعيل الخاص بك لربط النظام بهذا الجهاز.
                </p>

                <form onSubmit={handleActivate} className="space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-100">
                            {error}
                        </div>
                    )}
                    
                    <div className="relative text-right">
                        <label className="block text-sm font-bold text-slate-700 mb-2">البريد الإلكتروني للحساب</label>
                        <div className="relative">
                            <input
                                type="email"
                                dir="ltr"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-white border border-slate-300 rounded-xl p-3 pl-10 text-left focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all text-slate-800"
                                placeholder="name@domain.com"
                                disabled={isLoading}
                            />
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        </div>
                    </div>

                    <div className="relative text-right">
                        <label className="block text-sm font-bold text-slate-700 mb-2">كلمة المرور</label>
                        <div className="relative">
                            <input
                                type="password"
                                dir="ltr"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-white border border-slate-300 rounded-xl p-3 pl-10 text-left focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all text-slate-800"
                                placeholder="********"
                                disabled={isLoading}
                            />
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        </div>
                    </div>

                    <div className="relative text-right pt-2">
                        <label className="block text-sm font-bold text-slate-700 mb-2">كود التفعيل (License Code)</label>
                        <div className="relative">
                            <input
                                type="text"
                                dir="ltr"
                                value={licenseCode}
                                onChange={(e) => setLicenseCode(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-4 pl-12 text-center font-bold tracking-widest uppercase focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all text-slate-800"
                                placeholder="FLY-XXXX-XXXX"
                                disabled={isLoading}
                            />
                            <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-fly-600 hover:bg-fly-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-fly-600/20 transition-all flex items-center justify-center gap-2"
                    >
                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'تفعيل النظام الآن'}
                    </button>
                </form>

                <p className="mt-8 text-xs text-slate-400">
                    للحصول على كود التفعيل، يرجى التواصل مع الدعم الفني.
                </p>
            </div>
        </div>
    );
};

export default ActivationScreen;
